import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronRight, Car, Users, Tag, ShieldCheck, Clock, CreditCard } from 'lucide-react';
import api from '@/lib/axios';
import { useSeo, useJsonLd } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';

const SITE = 'https://marketplacetvde.pt';

const INCLUIDO = [
  'Anúncio visível em todo o Portugal',
  'Contactos por email e telefone confirmados',
  'Página própria do anúncio (partilhável)',
  'Revisão do anúncio antes de ficar online',
  'Gestão do anúncio a qualquer momento',
];

export default function MarketplacePricingPage() {
  const [settings, setSettings] = useState(null);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: 'Preços — Anunciar no Marketplace TVDE | Preçário',
    description:
      'Preçário simples e transparente para anunciar viaturas e motoristas TVDE em Portugal. Pagamento único por anúncio, sem mensalidades.',
    path: '/precos',
  });
  useJsonLd('breadcrumb-precos', {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Início', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'Preços', item: `${SITE}/precos` },
    ],
  });

  useEffect(() => {
    let active = true;
    api
      .get('/marketplace/settings')
      .then((res) => { if (active) setSettings(res.data); })
      .catch(() => { if (active) setSettings(null); })
      .finally(() => { if (active) setLoading(false); });
    api
      .get('/marketplace/produtos')
      .then((res) => { if (active) setCatalogo(res.data.items || []); })
      .catch(() => { if (active) setCatalogo([]); });
    return () => { active = false; };
  }, []);

  const moeda = settings?.moeda === 'EUR' ? '€' : (settings?.moeda || '€');
  const dias = settings?.duracao_dias ?? 30;
  const campanha = !!settings?.campanha_gratuita_ativa;
  const campanhaDias = settings?.campanha_gratuita_dias ?? 30;
  const packs = settings?.packs || [];
  // Os preços vêm exclusivamente do catálogo público. Sem produto com preço, não há preço a mostrar.
  const catViatura = catalogo.filter((p) => ['publicacao_aluguer', 'publicacao_venda'].includes(p.tipo));
  const precoViatura = catViatura.length
    ? Math.min(...catViatura.map((p) => Number(p.preco_final) || 0))
    : null;

  return (
    <div className="relative min-h-screen bg-slate-50 text-slate-900" data-testid="marketplace-pricing-page">
      <nav className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur-md">
        <div className="mx-auto flex w-full max-w-[1400px] items-center justify-between gap-3 px-4 py-3 md:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="pricing-logo-home">
            <img src="/icon-192x192.png" alt="Marketplace TVDE" className="h-8 w-8 rounded-lg object-cover" />
            <span className="text-base font-bold">Marketplace TVDE</span>
          </Link>
          <Link
            to="/"
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            data-testid="pricing-back-home"
          >
            Ver anúncios
          </Link>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-5xl px-4 py-10 md:px-8">
        <nav aria-label="Breadcrumb" className="mb-5 flex items-center gap-1 text-sm text-slate-500" data-testid="pricing-breadcrumb">
          <Link to="/" className="hover:text-emerald-600">Início</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="font-semibold text-slate-700">Preços</span>
        </nav>

        <header className="mb-8 text-center">
          <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl" data-testid="pricing-h1">
            Preçário simples e transparente
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-base text-slate-600">
            Pague apenas uma vez por anúncio. Sem mensalidades, sem comissões sobre negócios.
            {settings?.texto_comercial ? ` ${settings.texto_comercial}` : ''}
          </p>
          {settings && (
            <p className="mx-auto mt-2 max-w-2xl text-sm font-medium text-slate-500" data-testid="pricing-payment-note">
              {!settings.pagamentos_disponiveis
                ? 'Os métodos de pagamento serão disponibilizados quando terminar o período gratuito.'
                : (settings.metodos_pagamento?.length
                    ? `Pagamento por ${settings.metodos_pagamento.join(', ')}.`
                    : 'Pagamento seguro no final da subscrição.')}
            </p>
          )}
        </header>

        {loading ? (
          <p className="text-center text-slate-500" data-testid="pricing-loading">A carregar preços…</p>
        ) : (
          <>
            {campanha && (
              <div
                className="mb-8 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-center"
                data-testid="pricing-campaign-banner"
              >
                <p className="text-lg font-extrabold text-amber-700">
                  🎉 Lançamento: GRÁTIS durante {campanhaDias} dias
                </p>
                <p className="mt-1 text-sm text-amber-700">
                  Aproveite para publicar viaturas e motoristas sem qualquer custo durante o período de lançamento.
                </p>
              </div>
            )}

            <div className="mx-auto grid max-w-3xl gap-6 md:grid-cols-2">
              {/* Card viatura */}
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="pricing-card-viatura">
                <div className="flex items-center gap-2 text-emerald-600">
                  <Car className="h-5 w-5" />
                  <span className="text-sm font-bold uppercase tracking-wide">Anúncio de Viatura</span>
                </div>
                <div className="mt-4 flex items-end gap-2">
                  {precoViatura == null ? (
                    <span className="text-4xl font-extrabold text-emerald-600" data-testid="pricing-viatura-value">Grátis</span>
                  ) : campanha ? (
                    <>
                      <span className="text-4xl font-extrabold text-emerald-600" data-testid="pricing-viatura-value">Grátis</span>
                      <span className="mb-1 text-sm text-slate-500">depois {precoViatura.toFixed(2)} {moeda}</span>
                    </>
                  ) : (
                    <span className="text-4xl font-extrabold text-slate-900" data-testid="pricing-viatura-value">{precoViatura.toFixed(2)} {moeda}</span>
                  )}
                </div>
                <p className="mt-1 text-sm text-slate-500">
                  {precoViatura == null
                    ? 'Sem custos neste momento · preço ainda por definir'
                    : `Pagamento único · anúncio ativo ${dias} dias`}
                </p>
                <ul className="mt-5 space-y-2">
                  {INCLUIDO.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/anunciar-viatura"
                  className="mt-6 flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-3 text-sm font-bold text-white transition hover:bg-emerald-600"
                  data-testid="pricing-cta-viatura"
                >
                  <Car className="h-4 w-4" /> Anunciar viatura
                </Link>
              </div>

              {/* Card motorista */}
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="pricing-card-motorista">
                <div className="flex items-center gap-2 text-emerald-600">
                  <Users className="h-5 w-5" />
                  <span className="text-sm font-bold uppercase tracking-wide">Perfil de Motorista</span>
                </div>
                <div className="mt-4 flex items-end gap-2">
                  <span className="text-4xl font-extrabold text-emerald-600" data-testid="pricing-motorista-value">Gratuito</span>
                </div>
                <p className="mt-1 text-sm text-slate-500" data-testid="pricing-motorista-note">
                  Sempre gratuito · publicar que procura viatura nunca tem custos
                </p>
                <ul className="mt-5 space-y-2">
                  {INCLUIDO.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-500" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/publicar-procura"
                  className="mt-6 flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50"
                  data-testid="pricing-cta-motorista"
                >
                  <Users className="h-4 w-4" /> Publicar procura
                </Link>
              </div>
            </div>

            {/* Packs (só quando os pagamentos estiverem ativos) */}
            {packs.length > 0 && settings?.comercio_ativo && (
              <section className="mt-12" data-testid="pricing-packs">
                <h2 className="mb-4 text-center text-xl font-bold text-slate-900">Packs de anúncios</h2>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {packs.map((p, i) => (
                    <div key={i} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`pricing-pack-${i}`}>
                      <div className="flex items-center gap-2 text-emerald-600">
                        <Tag className="h-4 w-4" />
                        <span className="text-sm font-bold">{p.label}</span>
                      </div>
                      <p className="mt-3 text-3xl font-extrabold text-slate-900">{p.price}{moeda}</p>
                      <p className="mt-1 text-sm text-slate-500">{p.quantity} anúncios · {p.days} dias cada</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Garantias */}
            <section className="mt-12 grid gap-4 sm:grid-cols-3" data-testid="pricing-guarantees">
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                <CreditCard className="h-6 w-6 flex-shrink-0 text-emerald-500" />
                <div>
                  <p className="text-sm font-bold text-slate-900">
                    {settings?.comercio_ativo ? 'Pagamento seguro' : 'Publicar é gratuito'}
                  </p>
                  <p className="text-xs text-slate-500" data-testid="pricing-payment-card-note">
                    {settings?.pagamentos_disponiveis
                      ? (settings.metodos_pagamento?.length
                          ? `${settings.metodos_pagamento.join(', ')} via Stripe.`
                          : 'Pagamento seguro via Stripe.')
                      : 'Métodos de pagamento disponíveis quando terminar o período gratuito.'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                <ShieldCheck className="h-6 w-6 flex-shrink-0 text-emerald-500" />
                <div>
                  <p className="text-sm font-bold text-slate-900">Anúncios revistos</p>
                  <p className="text-xs text-slate-500">Cada anúncio é analisado antes de ficar online.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                <Clock className="h-6 w-6 flex-shrink-0 text-emerald-500" />
                <div>
                  <p className="text-sm font-bold text-slate-900">Sem mensalidades</p>
                  <p className="text-xs text-slate-500">Paga só quando publica. Sem renovações automáticas.</p>
                </div>
              </div>
            </section>
          </>
        )}
        {catalogo.length > 0 && (
          <section className="mt-12" data-testid="pricing-catalog">
            <h2 className="text-base font-bold md:text-lg">Preçário detalhado</h2>
            <p className="mt-1 text-sm text-slate-600">
              Valores definidos no catálogo do Marketplace. Produtos ainda sem preço decidido não aparecem aqui.
            </p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[560px] text-left text-sm">
                <thead className="text-[10px] uppercase text-slate-400">
                  <tr><th className="py-2">Produto</th><th>Duração</th><th>Sem IVA</th><th>IVA</th><th>Total</th></tr>
                </thead>
                <tbody>
                  {catalogo.map((p) => (
                    <tr key={p.code} className="border-t border-slate-100" data-testid={`pricing-product-${p.code}`}>
                      <td className="py-2">
                        <p className="font-semibold text-slate-800">{p.nome}</p>
                        {p.descricao && <p className="text-xs text-slate-500">{p.descricao}</p>}
                      </td>
                      <td className="text-xs">{p.duracao_dias ? `${p.duracao_dias} dias` : '—'}</td>
                      <td className="text-xs">{p.preco_sem_iva.toFixed(2)} €</td>
                      <td className="text-xs">{p.taxa_iva}%</td>
                      <td className="text-sm font-bold">{p.preco_final.toFixed(2)} €</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </main>

      <MarketplaceFooter onManageClick={() => (window.location.href = '/gerir-anuncio')} />
    </div>
  );
}
